import {
   ADDITIONAL_SEAT_COST,
   BASE_SEAT_COST,
   EAC_COLD_LEAD,
   EAC_NEW_LEAD,
} from '@forwardflow/constants/onboarding.constants'
import { useSubscriptionPackageLists } from '@forwardflow/hooks/customHooks'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useEffect, useMemo } from 'react'

export const usePricing = () => {
   const { onboardingData, setPricingData } = useOnboardingStoreV2()
   const { data: subscriptionPackages } = useSubscriptionPackageLists({
      page: 1,
      limit: 10,
   })

   const { pricingData } = onboardingData

   const { seatsCount, newLeadCount, reactivateLeadCount } = pricingData || {
      seatsCount: 1,
      newLeadCount: 100,
      reactivateLeadCount: 200,
   }

   // Calculate seat cost: $97 for first user + $50 for each additional user
   const seatCost = useMemo(() => {
      return BASE_SEAT_COST + (seatsCount - 1) * ADDITIONAL_SEAT_COST
   }, [seatsCount])

   // Calculate nurture cost: newLeadCount * $0.62
   const newLeadsCost = useMemo(() => {
      return (newLeadCount * EAC_NEW_LEAD).toFixed(2)
   }, [newLeadCount])

   // Calculate revival cost: reactivateLeadCount * $0.44
   const coldLeadsCost = useMemo(() => {
      return (reactivateLeadCount * EAC_COLD_LEAD).toFixed(2)
   }, [reactivateLeadCount])

   // Calculate total cost
   const totalCost = useMemo(() => {
      return (
         Number(seatCost) +
         Number(newLeadsCost) +
         Number(coldLeadsCost)
      ).toFixed(2)
   }, [seatCost, newLeadsCost, coldLeadsCost])

   useEffect(() => {
      if (subscriptionPackages) {
         const selectedTier = subscriptionPackages?.pricingTiers?.[0]
         if (selectedTier) {
            setPricingData({ ...pricingData, selectedTierId: selectedTier.id })
         }
      }
   }, [setPricingData, subscriptionPackages])

   // Handler to update pricing data
   const handlePricingDataChange = (
      field: 'seatsCount' | 'newLeadCount' | 'reactivateLeadCount',
      value: number
   ) => {
      setPricingData({
         ...pricingData,
         [field]: value,
      })
   }

   return {
      // Constants
      EAC_NEW_LEAD,
      EAC_COLD_LEAD,

      // Pricing data
      seatsCount,
      newLeadCount,
      reactivateLeadCount,

      // Calculated costs
      seatCost,
      newLeadsCost,
      coldLeadsCost,
      totalCost,

      // Handlers
      handlePricingDataChange,
   }
}
